import React, { useState, useEffect, useCallback, useRef } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LionsStripe from '../components/LionsStripe';
import {
  PhotoIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlayIcon,
  PauseIcon,
  ArrowsPointingOutIcon,
} from '@heroicons/react/24/outline';

const images = [
  { src: '/assets/images/gallery/IMG_3925.jpg', alt: 'The Lexington Academy — School Life' },
  { src: '/assets/images/gallery/IMG_3928.jpg', alt: 'The Lexington Academy — School Life' },
  { src: '/assets/images/gallery/IMG_3930.jpg', alt: 'The Lexington Academy — School Life' },
  { src: '/assets/images/gallery/IMG_3932.jpg', alt: 'The Lexington Academy — School Life' },
  { src: '/assets/images/gallery/IMG_3934.jpg', alt: 'The Lexington Academy — School Life' },
  { src: '/assets/images/gallery/awards-ceremony.jpg', alt: 'The Lexington Academy — Awards Ceremony' },
  { src: '/assets/images/gallery/so-fun-game-show-stage.jpg', alt: 'The Lexington Academy — So Fun Game Show' },
  { src: '/assets/images/gallery/so-fun-game-show-auditorium.jpg', alt: 'The Lexington Academy — So Fun Game Show Auditorium' },
  { src: '/assets/images/gallery/so-fun-game-show-performance.jpg', alt: 'The Lexington Academy — So Fun Game Show Performance' },
  { src: '/assets/images/gallery/student-showcase-group.jpg', alt: 'The Lexington Academy — Student Showcase' },
  { src: '/assets/images/gallery/student-showcase-science.jpg', alt: 'The Lexington Academy — Student Science Showcase' },
  { src: '/assets/images/gallery/high-school-fair-students.jpg', alt: 'The Lexington Academy — Students collecting information from high school tables at the High School Fair' },
  { src: '/assets/images/gallery/high-school-fair-information-table.jpg', alt: 'The Lexington Academy — Students learning about a high school at an information table' },
  { src: '/assets/images/gallery/high-school-fair-representative.jpg', alt: 'The Lexington Academy — Students speaking with a high school representative at the High School Fair' },
  { src: '/assets/images/gallery/5th-grade-girls-with-principal.jpg', alt: 'The Lexington Academy — Our 5th grade girls reading and writing with Dr. Smith at a picnic table in the schoolyard' },
  { src: '/assets/images/gallery/spirit-week-student-earmuffs-peace-sign.jpg', alt: 'The Lexington Academy — Spirit Week: a young student in pink earmuffs with candies in her hair buns flashing a peace sign in the hallway' },
  { src: '/assets/images/gallery/spirit-week-pajama-day-read-aloud.jpg', alt: 'The Lexington Academy — Spirit Week: students in pajamas sitting on the classroom rug as a teacher reads a Halloween picture book aloud' },
  { src: '/assets/images/gallery/spirit-week-teacher-and-student-hallway.jpg', alt: 'The Lexington Academy — Spirit Week: a teacher in a Lexington Lions Staff hoodie and a student in a cap, glasses and work jacket posing in the hallway' },
  { src: '/assets/images/gallery/spirit-week-student-and-teacher-classroom.jpg', alt: 'The Lexington Academy — Spirit Week: a student in a shirt, tie and glasses posing playfully next to a teacher in a Lexington hoodie in the classroom' },
  { src: '/assets/images/gallery/spirit-week-lorax-costume.jpg', alt: 'The Lexington Academy — Spirit Week: a student dressed as the Lorax in orange with a yellow mustache beside a costumed teacher holding a shovel' },
  { src: '/assets/images/gallery/monthly-parent-popup-prek-pumpkins.jpg', alt: 'The Lexington Academy — Monthly Parent Popup with Pre-K: children at a classroom table with pumpkins and plates of a fall snack as a family member reaches in to help' },
];

const AUTOPLAY_MS = 5000;

const GalleryPage = () => {
  const [current, setCurrent] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const thumbRefs = useRef([]);

  const goTo = useCallback((index) => {
    setCurrent(((index % images.length) + images.length) % images.length);
  }, []);

  const prev = useCallback(() => {
    setCurrent((i) => (i - 1 + images.length) % images.length);
  }, []);

  const next = useCallback(() => {
    setCurrent((i) => (i + 1) % images.length);
  }, []);

  const openLightbox = () => {
    setIsPlaying(false);
    setLightboxOpen(true);
  };
  const closeLightbox = () => setLightboxOpen(false);

  // Autoplay
  useEffect(() => {
    if (!isPlaying || lightboxOpen) return;
    const id = setInterval(next, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [isPlaying, lightboxOpen, next]);

  // Keyboard navigation (works for both slideshow and lightbox)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prev, next]);

  // Keep the active thumbnail in view
  useEffect(() => {
    const el = thumbRefs.current[current];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [current]);

  return (
    <div className="min-h-screen bg-accent-light font-sans selection:bg-secondary selection:text-white">
      <Navbar />

      <main id="main-content">
        {/* ============================================================================
            HERO SECTION
        ============================================================================ */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-primary overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-primary via-navy-900 to-primary"></div>
            <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay"></div>
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[100px]"></div>
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-[100px]"></div>
          </div>

          <div className="max-w-4xl mx-auto px-4 text-center relative z-10 bg-primary rounded-2xl py-1">
            <div className="inline-flex items-center justify-center p-3 bg-white/10 backdrop-blur rounded-full mb-8 border border-white/20">
              <PhotoIcon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight">
              Our <span className="text-secondary">Gallery</span>
            </h1>
            <p className="text-xl text-white/80 font-light max-w-2xl mx-auto">
              A glimpse into the vibrant life and community of The Lexington Academy.
            </p>
          </div>
          <LionsStripe className="absolute inset-x-0 bottom-0 z-10" />
        </section>

        {/* ============================================================================
            SLIDESHOW
        ============================================================================ */}
        <section className="py-20 -mt-12 relative z-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-orange-700 font-bold tracking-widest text-sm uppercase mb-2 block">School Life</span>
              <h2 className="text-4xl md:text-5xl font-extrabold text-primary tracking-tight">
                Moments That Matter
              </h2>
              <p className="mt-4 text-lg text-gray-600 max-w-xl mx-auto">
                Sit back and watch, or use the arrows to explore at your own pace.
              </p>
            </div>

            {/* Main stage */}
            <div
              className="group relative aspect-[16/10] sm:aspect-[16/9] w-full overflow-hidden rounded-3xl bg-primary shadow-2xl border border-gray-100"
              role="region"
              aria-roledescription="carousel"
              aria-label="Photo slideshow"
            >
              {/* Slides (crossfade) */}
              {images.map((image, index) => (
                <img
                  key={index}
                  src={image.src}
                  alt={image.alt}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  aria-hidden={index !== current}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${
                    index === current ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}
                />
              ))}

              {/* Bottom caption plate — solid so contrast against the photo is
                  actually measurable, not a gradient fade axe can't resolve */}
              <div className="absolute inset-x-0 bottom-0 bg-primary/85 p-6 sm:p-8 pointer-events-none">
                <p className="text-white text-base sm:text-lg font-medium drop-shadow">
                  {images[current].alt}
                </p>
                <p className="text-white/70 text-sm mt-1">
                  {current + 1} / {images.length}
                </p>
              </div>

              {/* Expand to fullscreen */}
              <button
                onClick={openLightbox}
                className="absolute top-4 right-4 p-2.5 rounded-full bg-black/30 hover:bg-black/50 text-white backdrop-blur transition-all opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/60"
                aria-label="View full size"
              >
                <ArrowsPointingOutIcon className="w-5 h-5" />
              </button>

              {/* Prev / Next arrows */}
              <button
                onClick={prev}
                className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 p-2.5 sm:p-3 rounded-full bg-white/20 hover:bg-white/40 text-white backdrop-blur transition-all opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/60"
                aria-label="Previous photo"
              >
                <ChevronLeftIcon className="w-6 h-6" />
              </button>
              <button
                onClick={next}
                className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 p-2.5 sm:p-3 rounded-full bg-white/20 hover:bg-white/40 text-white backdrop-blur transition-all opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/60"
                aria-label="Next photo"
              >
                <ChevronRightIcon className="w-6 h-6" />
              </button>

              {/* Play / Pause */}
              <button
                onClick={() => setIsPlaying((p) => !p)}
                className="absolute bottom-4 right-4 p-2.5 rounded-full bg-black/30 hover:bg-black/50 text-white backdrop-blur transition-all focus:outline-none focus:ring-2 focus:ring-white/60"
                aria-label={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
              >
                {isPlaying ? <PauseIcon className="w-5 h-5" /> : <PlayIcon className="w-5 h-5" />}
              </button>
            </div>

            {/* Dots */}
            <div className="flex flex-wrap items-center justify-center gap-0 mt-6">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goTo(index)}
                  aria-label={`Go to photo ${index + 1}`}
                  aria-current={index === current}
                  className="group flex h-6 min-w-6 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary/50"
                >
                  {/* 24px hit area (WCAG 2.5.8); the visible dot stays 10px */}
                  <span
                    aria-hidden="true"
                    className={`block h-2.5 rounded-full transition-all duration-300 ${
                      index === current ? 'w-8 bg-secondary' : 'w-2.5 bg-gray-300 group-hover:bg-gray-400'
                    }`}
                  />
                </button>
              ))}
            </div>

            {/* Thumbnail strip */}
            <div className="mt-8 flex gap-3 overflow-x-auto pb-3 px-1 snap-x snap-mandatory">
              {images.map((image, index) => (
                <button
                  key={index}
                  ref={(el) => (thumbRefs.current[index] = el)}
                  onClick={() => goTo(index)}
                  aria-label={`View ${image.alt}`}
                  aria-current={index === current}
                  className={`relative flex-shrink-0 w-28 sm:w-32 aspect-[4/3] overflow-hidden rounded-xl snap-center transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-secondary/60 ${
                    index === current
                      ? 'ring-4 ring-secondary scale-100'
                      : 'ring-1 ring-gray-200 opacity-60 hover:opacity-100'
                  }`}
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>

            <p className="text-center text-sm text-gray-600 mt-8">
              {images.length} photo{images.length !== 1 ? 's' : ''} &mdash; More coming soon
            </p>
          </div>
        </section>

        {/* ============================================================================
            CTA SECTION
        ============================================================================ */}
        <section className="py-20 md:py-24 bg-accent-yellow">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl p-12 md:p-16 shadow-2xl text-center relative overflow-hidden bg-primary">
              <LionsStripe className="absolute top-0 inset-x-0" />
              <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight mb-6">Be Part of Our Story</h2>
              <p className="text-lg text-white/90 mb-10 max-w-2xl mx-auto">
                Want to learn more about life at The Lexington Academy? Reach out to us or explore what we have to offer.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 text-base font-extrabold text-primary bg-accent-yellow rounded-full hover:bg-white shadow-lg hover:shadow-xl hover:-translate-y-1 motion-reduce:transform-none transition-all duration-300"
                >
                  Contact Us
                </a>
                <a
                  href="/mission-statement"
                  className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white border-2 border-white/50 rounded-full hover:border-white hover:bg-white/10 transition-all duration-300"
                >
                  Our Mission
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* ============================================================================
          LIGHTBOX (fullscreen view of current slide)
      ============================================================================ */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Image lightbox"
          onClick={closeLightbox}
        >
          {/* Close */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
            aria-label="Close lightbox"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>

          {/* Prev */}
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
            aria-label="Previous photo"
          >
            <ChevronLeftIcon className="w-6 h-6" />
          </button>

          {/* Image */}
          <div className="max-w-5xl w-full mx-16 flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={images[current].src}
              alt={images[current].alt}
              className="max-h-[80vh] w-auto rounded-2xl shadow-2xl object-contain"
            />
            <p className="text-white/60 text-sm">
              {current + 1} / {images.length}
            </p>
          </div>

          {/* Next */}
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
            aria-label="Next photo"
          >
            <ChevronRightIcon className="w-6 h-6" />
          </button>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
